import { useState, useCallback, useEffect, useMemo } from "react";
import { FiPlus } from "react-icons/fi";
import debounce from "lodash.debounce";
import { Button } from "newui/components/Button";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import { TextInputWithLabel } from "newui/components/ui/InputWithLabel/TextInputWithLabel";
import type {
	AddPanelMemberPayload,
	AiPanelUser,
	CompanyUser,
	EvaluationPanelMember,
	PanelMode,
} from "../../../types/panel.type";
import {
	searchCompanyUsers,
	getAiPanelUsers,
} from "../../../api/panel-api";

interface AddMemberFormProps {
	companyId: number | null;
	panelMode: PanelMode;
	rfxId: number;
	panelMembers: EvaluationPanelMember[];
	onAddMember: (payload: AddPanelMemberPayload) => Promise<void>;
}

export const AddMemberForm: React.FC<AddMemberFormProps> = ({
	companyId,
	panelMode,
	rfxId,
	panelMembers,
	onAddMember,
}) => {
	const [companyUsers, setCompanyUsers] = useState<SelectOption[]>([]);
	const [companyUserEmailMap, setCompanyUserEmailMap] = useState<Map<number, string>>(new Map());
	const [isSearchingUsers, setIsSearchingUsers] = useState(false);
	const [selectedCompanyUser, setSelectedCompanyUser] = useState<SelectOption | null>(null);

	const [externalEmail, setExternalEmail] = useState("");
	const [externalFirstName, setExternalFirstName] = useState("");
	const [externalLastName, setExternalLastName] = useState("");
	const [emailError, setEmailError] = useState<string | null>(null);

	const [selectedAiUser, setSelectedAiUser] = useState<SelectOption | null>(null);
	const [isLoadingAi, setIsLoadingAi] = useState(false);

	const [isAdding, setIsAdding] = useState(false);

	const showAiSection = panelMode === "ai_panel";

	const addedEmails = useMemo(
		() => new Set(panelMembers.map((m) => m.email.toLowerCase())),
		[panelMembers]
	);

	const addedAiUserIds = useMemo(
		() => new Set(
			panelMembers
				.filter((m) => m.member_type === "ai" && m.ai_panel_user)
				.map((m) => m.ai_panel_user!.id)
		),
		[panelMembers]
	);

	const availableCompanyUsers = useMemo(
		() => companyUsers.filter((u) => {
			const email = companyUserEmailMap.get(u.value as number);
			return !email || !addedEmails.has(email);
		}),
		[companyUsers, companyUserEmailMap, addedEmails]
	);

	const updateCompanyUsers = (users: CompanyUser[]): void => {
		const options = users.map((u) => ({
			label: `${u.full_name} (${u.email})`,
			value: u.id,
		}));
		const emailMap = new Map<number, string>();
		users.forEach((u) => {
			emailMap.set(u.id, u.email.toLowerCase());
		});
		setCompanyUsers(options);
		setCompanyUserEmailMap(emailMap);
	};

	// Load all company users on mount
	useEffect(() => {
		if (!companyId) {
			return;
		}
		setIsSearchingUsers(true);
		searchCompanyUsers(companyId, "")
			.then((users) => {
				updateCompanyUsers(users);
			})
			.catch(() => {
				setCompanyUsers([]);
			})
			.finally(() => {
				setIsSearchingUsers(false);
			});
	}, [companyId]);

	const [allAiPanelUsers, setAllAiPanelUsers] = useState<SelectOption[]>([]);

	useEffect(() => {
		if (showAiSection) {
			setIsLoadingAi(true);
			getAiPanelUsers(rfxId)
				.then((users) => {
					const options = users
						.filter((u) => u.is_active)
						.map((u) => ({
							label: `${u.name} — ${u.description}`,
							value: u.id,
						}));
					setAllAiPanelUsers(options);
				})
				.catch(() => {
					setAllAiPanelUsers([]);
				})
				.finally(() => {
					setIsLoadingAi(false);
				});
		}
	}, [showAiSection, rfxId]);

	const availableAiUsers = useMemo(
		() => allAiPanelUsers.filter((u) => !addedAiUserIds.has(u.value as number)),
		[allAiPanelUsers, addedAiUserIds]
	);

	const debouncedSearch = useCallback(
		debounce(async (query: string) => {
			if (!companyId) {
				setCompanyUsers([]);
				setIsSearchingUsers(false);
				return;
			}
			try {
				const users = await searchCompanyUsers(companyId, query);
				updateCompanyUsers(users);
			} catch {
				setCompanyUsers([]);
			} finally {
				setIsSearchingUsers(false);
			}
		}, 300),
		[companyId]
	);

	const handleCompanyUserSearch = (inputValue: string) => {
		setIsSearchingUsers(true);
		debouncedSearch(inputValue);
	};

	const handleAddInternal = async () => {
		if (!selectedCompanyUser) {
			return;
		}
		setIsAdding(true);
		try {
			await onAddMember({
				type: "internal",
				user_id: selectedCompanyUser.value as number,
			});
			setSelectedCompanyUser(null);
		} finally {
			setIsAdding(false);
		}
	};

	const handleAddExternal = async () => {
		const trimmedEmail = externalEmail.trim();
		const trimmedFirst = externalFirstName.trim();
		const trimmedLast = externalLastName.trim();
		if (!trimmedEmail || !trimmedFirst) {
			return;
		}
		setEmailError(null);
		if (addedEmails.has(trimmedEmail.toLowerCase())) {
			setEmailError("This email has already been added to the panel.");
			return;
		}
		const fullName = `${trimmedFirst} ${trimmedLast}`.trim();
		setIsAdding(true);
		try {
			await onAddMember({
				type: "external",
				email: trimmedEmail,
				name: fullName,
			});
			setExternalEmail("");
			setExternalFirstName("");
			setExternalLastName("");
		} finally {
			setIsAdding(false);
		}
	};

	const handleAddAi = async () => {
		if (!selectedAiUser) {
			return;
		}
		setIsAdding(true);
		try {
			await onAddMember({
				type: "ai",
				ai_panel_user_id: selectedAiUser.value as number,
			});
			setSelectedAiUser(null);
		} finally {
			setIsAdding(false);
		}
	};

	return (
		<div className="space-y-4" data-testid="add-member-form">
			<h3 className="text-md font-medium text-gray-700">Add Panel Members</h3>

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				{/* Add from company */}
				<div>
					<SelectWithLabel
						name="company-user-search"
						label="Add from company"
						placeholder="Search by name or email..."
						options={availableCompanyUsers}
						value={selectedCompanyUser}
						onChange={(option) => setSelectedCompanyUser(option as SelectOption | null)}
						onInputChange={handleCompanyUserSearch}
						isLoading={isSearchingUsers}
						testId="company-user-select"
						clearAble
					/>
					<Button
						btntype="primary"
						size="sm"
						onClick={handleAddInternal}
						disabled={!selectedCompanyUser || isAdding}
						isLoading={isAdding}
						className="mt-2"
						data-testid="add-internal-btn"
					>
						<span className="flex items-center gap-1">
							<FiPlus size={14} />
							Add
						</span>
					</Button>
				</div>

				{/* Invite by email */}
				<div>
					<TextInputWithLabel
						name="external-email"
						label="Invite by email"
						value={externalEmail}
						onChange={(e) => {
							setExternalEmail(e.target.value);
							if (emailError) {
								setEmailError(null);
							}
						}}
						placeholder="email@example.com"
						testId="external-email-input"
						errorText={emailError}
					/>
					<div className="mt-2 grid grid-cols-2 gap-2">
						<TextInputWithLabel
							name="external-first-name"
							label="First name"
							value={externalFirstName}
							onChange={(e) => setExternalFirstName(e.target.value)}
							placeholder="First name"
							testId="external-first-name-input"
						/>
						<TextInputWithLabel
							name="external-last-name"
							label="Last name"
							value={externalLastName}
							onChange={(e) => setExternalLastName(e.target.value)}
							placeholder="Last name"
							testId="external-last-name-input"
						/>
					</div>
					<Button
						btntype="primary"
						size="sm"
						onClick={handleAddExternal}
						disabled={!externalEmail.trim() || !externalFirstName.trim() || isAdding}
						isLoading={isAdding}
						className="mt-2"
						data-testid="add-external-btn"
					>
						<span className="flex items-center gap-1">
							<FiPlus size={14} />
							Invite
						</span>
					</Button>
				</div>
			</div>

			{/* AI persona selection */}
			{showAiSection && (
				<div className="max-w-md">
					<SelectWithLabel
						label="Add AI Persona"
						placeholder="Select an AI persona..."
						options={availableAiUsers}
						value={selectedAiUser}
						onChange={(option) => setSelectedAiUser(option as SelectOption | null)}
						isLoading={isLoadingAi}
						testId="ai-user-select"
					/>
					<Button
						btntype="primary"
						size="sm"
						onClick={handleAddAi}
						disabled={!selectedAiUser || isAdding}
						isLoading={isAdding}
						className="mt-2"
						data-testid="add-ai-btn"
					>
						<span className="flex items-center gap-1">
							<FiPlus size={14} />
							Add AI Persona
						</span>
					</Button>
				</div>
			)}
		</div>
	);
};
